import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { LocationNodeData } from '../../../../../@types/live-search.type';
import { useSearchNodeDispatch } from '../../../../../queries/live-search-query';
import { labelSx } from './PartsShipmentsHelpers';

export interface NodeSearchFieldProps {
  label: string;
  defaultName?: string;
  onChange: (node: LocationNodeData | null) => void;
}

export const NodeSearchField: React.FC<NodeSearchFieldProps> = ({
  label, defaultName, onChange,
}) => {
  const [inputValue, setInputValue] = useState(defaultName ?? '');
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [selected, setSelected] = useState<LocationNodeData | null>(null);
  const searchNodes = useSearchNodeDispatch();

  useEffect(() => {
    if (inputValue.length < 2) { setOptions([]); return; }
    const timer = setTimeout(() => {
      searchNodes.mutateAsync({ name: inputValue })
        .then(setOptions)
        .catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      <Autocomplete
        size="small"
        options={options}
        getOptionLabel={(opt) => opt.name}
        filterOptions={(x) => x}
        value={selected}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        onChange={(_, val) => { setSelected(val); onChange(val); }}
        loading={searchNodes.isPending}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={`Search ${label.toLowerCase()}...`}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={`${option.id}-${option.code}`}>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                {option.name}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                {option.nodeTypeName} — {option.code}
                {option.locationHierarchy ? ` · ${option.locationHierarchy}` : ''}
                {option.clientContractName ? ` · ${option.clientContractName}` : ''}
              </Typography>
            </Box>
          </li>
        )}
      />
    </Box>
  );
};
